import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Mail, Clock, X, Shield, Users } from "lucide-react";
import { usePendingInvitations, PendingInvitation } from "@/hooks/usePendingInvitations";

export function PendingInvitationsList() {
  const { invitations, loading, cancelInvitation } = usePendingInvitations();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (invitations.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
        <Clock className="w-4 h-4" />
        Pending Invitations ({invitations.length})
      </h4>
      
      <div className="space-y-2">
        {invitations.map((invitation) => (
          <InvitationRow
            key={invitation.id}
            invitation={invitation}
            onCancel={cancelInvitation}
          />
        ))}
      </div>
    </div>
  );
}

interface InvitationRowProps {
  invitation: PendingInvitation;
  onCancel: (id: string) => Promise<boolean>;
}

function InvitationRow({ invitation, onCancel }: InvitationRowProps) {
  const getRoleIcon = (role: string) => {
    return role === "admin" ? (
      <Shield className="w-3 h-3 text-primary" />
    ) : (
      <Users className="w-3 h-3 text-muted-foreground" />
    );
  };

  const expiresIn = formatDistanceToNow(new Date(invitation.expiresAt), { addSuffix: false });

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border/50">
      <div className="p-2 rounded-full bg-primary/10">
        <Mail className="w-4 h-4 text-primary" />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{invitation.email}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            {getRoleIcon(invitation.role)}
            <span className="capitalize">{invitation.role}</span>
          </span>
          <span>•</span>
          <span>Expires in {expiresIn}</span>
        </div>
      </div>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
          >
            <X className="w-4 h-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel invitation?</AlertDialogTitle>
            <AlertDialogDescription>
              This will revoke the invitation sent to {invitation.email}. They will no longer be able to join the organization using this link.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Invitation</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => onCancel(invitation.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Cancel Invitation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
